import { inject, injectable } from 'inversify'

import { InjectionTokens } from '@/controller/tokens'

import { UniqueEntityID } from '@/domain/core'
import { UserGeometryChanged } from '@/domain/events/UserGeometryChanged'
import { UserGeometry } from '@/domain/models/UserGeometry/UserGeometry'
import { Result } from '@/domain/protocols/Result'

import {
  IUserGeometryRepository,
  UserGeometryFile,
  UserGeometryFiles,
  UserGeometryPendingChanges
} from './IUserGeometryRepository'
import { clearFlatGeobufReadCache } from './userGeometryFlatGeobuf'

import { DexieDB } from '@/infra/localDB/DexieDB'
import { DexieUserGeometry, UserGeometryFileKind } from '@/infra/localDB/IDexieDB'

@injectable()
export class UserGeometryRepository implements IUserGeometryRepository {
  constructor(@inject(InjectionTokens.DexieDB) private readonly localDB: DexieDB) {}

  async init(): Promise<Result<void>> {
    try {
      if (!this.localDB.isOpen()) {
        await this.localDB.open()
      }
      return Result.ok()
    } catch (error) {
      return Result.fail(`Failed to initialize UserGeometryRepository: ${error}`)
    }
  }

  async getGeometries(): Promise<Result<UserGeometry[]>> {
    try {
      const rows = await this.localDB.userGeometries.toArray()
      const geometries = rows.filter((row) => row.deleted === 0).map(this.toEntity)
      return Result.ok(geometries)
    } catch (error) {
      return Result.fail(`Failed to load user geometries: ${error}`)
    }
  }

  async getGeometry(id: UniqueEntityID): Promise<Result<UserGeometry>> {
    try {
      const row = await this.localDB.userGeometries.get(id.toString())
      if (!row || row.deleted === 1) {
        return Result.fail('Geometry not found')
      }
      return Result.ok(this.toEntity(row))
    } catch (error) {
      return Result.fail(`Failed to load user geometry: ${error}`)
    }
  }

  async saveGeometry(geometry: UserGeometry, files: UserGeometryFiles): Promise<Result<void>> {
    try {
      const id = geometry.id.toString()
      await this.localDB.transaction('rw', this.localDB.userGeometries, this.localDB.userGeometryFiles, async () => {
        await this.localDB.userGeometries.put(this.toRow(geometry))
        await this.localDB.userGeometryFiles.put({ id, kind: 'processed', blob: files.processed })
        await this.localDB.userGeometryFiles.put({
          id,
          kind: 'original',
          blob: files.original.blob,
          ext: files.original.ext
        })
      })
      UserGeometryChanged.next()
      return Result.ok()
    } catch (error) {
      return Result.fail(`Failed to save user geometry: ${error}`)
    }
  }

  async updateGeometry(geometry: UserGeometry): Promise<Result<void>> {
    try {
      const exists = await this.localDB.userGeometries.get(geometry.id.toString())
      if (!exists) {
        return Result.fail('Geometry not found')
      }

      geometry.synced = false
      geometry.updatedAt = new Date().toISOString()
      await this.localDB.userGeometries.put(this.toRow(geometry))
      UserGeometryChanged.next()
      return Result.ok()
    } catch (error) {
      return Result.fail(`Failed to update user geometry: ${error}`)
    }
  }

  async deleteGeometry(geometry: UserGeometry): Promise<Result<void>> {
    try {
      const exists = await this.localDB.userGeometries.get(geometry.id.toString())
      if (!exists) {
        return Result.fail('Geometry not found')
      }

      geometry.deleted = true
      geometry.synced = false
      geometry.updatedAt = new Date().toISOString()
      await this.localDB.userGeometries.put(this.toRow(geometry))
      UserGeometryChanged.next()
      return Result.ok()
    } catch (error) {
      return Result.fail(`Failed to delete user geometry: ${error}`)
    }
  }

  async existsByName(name: string): Promise<Result<boolean>> {
    try {
      const normalized = name.toLowerCase()
      const rows = await this.localDB.userGeometries.toArray()
      const exists = rows.some((row) => row.deleted === 0 && row.name.toLowerCase() === normalized)
      return Result.ok(exists)
    } catch (error) {
      return Result.fail(`Failed to check user geometry name: ${error}`)
    }
  }

  async existsByOriginalFileName(originalFileName: string): Promise<Result<boolean>> {
    try {
      const normalized = originalFileName.toLowerCase()
      const rows = await this.localDB.userGeometries.toArray()
      const exists = rows.some((row) => row.deleted === 0 && row.originalFileName.toLowerCase() === normalized)
      return Result.ok(exists)
    } catch (error) {
      return Result.fail(`Failed to check user geometry file: ${error}`)
    }
  }

  async getFile(id: UniqueEntityID, kind: UserGeometryFileKind): Promise<Result<UserGeometryFile | null>> {
    try {
      const row = await this.localDB.userGeometryFiles.get([id.toString(), kind])
      if (!row) {
        return Result.ok(null)
      }
      return Result.ok({ blob: row.blob, ext: row.ext })
    } catch (error) {
      return Result.fail(`Failed to read user geometry file: ${error}`)
    }
  }

  async hasFile(id: UniqueEntityID, kind: UserGeometryFileKind): Promise<Result<boolean>> {
    try {
      const row = await this.localDB.userGeometryFiles.get([id.toString(), kind])
      return Result.ok(Boolean(row?.blob))
    } catch (error) {
      return Result.fail(`Failed to check user geometry file: ${error}`)
    }
  }

  async putFile(id: UniqueEntityID, kind: UserGeometryFileKind, file: UserGeometryFile): Promise<Result<void>> {
    try {
      await this.localDB.userGeometryFiles.put({ id: id.toString(), kind, blob: file.blob, ext: file.ext })
      return Result.ok()
    } catch (error) {
      return Result.fail(`Failed to write user geometry file: ${error}`)
    }
  }

  async getPendingChanges(): Promise<Result<UserGeometryPendingChanges>> {
    try {
      const rows = await this.localDB.userGeometries.toArray()
      const upserts = rows.filter((row) => row.synced === 0 && row.deleted === 0).map(this.toEntity)
      const deletes = rows.filter((row) => row.synced === 0 && row.deleted === 1).map(this.toEntity)
      return Result.ok({ upserts, deletes })
    } catch (error) {
      return Result.fail(`Failed to read pending user geometry changes: ${error}`)
    }
  }

  async markSynced(id: string, expectedUpdatedAt: string | undefined): Promise<Result<boolean>> {
    try {
      const row = await this.localDB.userGeometries.get(id)
      if (!row) {
        return Result.ok(false)
      }
      // Optimistic guard: a mismatch means the row was edited after the sync push
      // started, so it must be re-pushed rather than marked synced.
      if (row.updatedAt !== expectedUpdatedAt) {
        return Result.ok(false)
      }

      await this.localDB.userGeometries.update(id, { synced: 1 })
      return Result.ok(true)
    } catch (error) {
      return Result.fail(`Failed to mark user geometry synced: ${error}`)
    }
  }

  async markFileSynced(id: string, kind: UserGeometryFileKind): Promise<Result<void>> {
    try {
      const row = await this.localDB.userGeometries.get(id)
      if (!row) {
        return Result.ok()
      }

      const originalSynced = kind === 'original' ? 1 : row.originalSynced
      const processedSynced = kind === 'processed' ? 1 : row.processedSynced
      const changes: Partial<DexieUserGeometry> = { originalSynced, processedSynced }

      // Auto-mark fully synced once both binaries have been uploaded.
      if (originalSynced === 1 && processedSynced === 1) {
        changes.synced = 1
      }

      await this.localDB.userGeometries.update(id, changes)
      return Result.ok()
    } catch (error) {
      return Result.fail(`Failed to mark user geometry file synced: ${error}`)
    }
  }

  async hardDelete(ids: string[]): Promise<Result<void>> {
    if (ids.length === 0) return Result.ok()
    try {
      const fileKeys: [string, UserGeometryFileKind][] = ids.flatMap((id) => [
        [id, 'processed'],
        [id, 'original']
      ])
      await this.localDB.transaction('rw', this.localDB.userGeometries, this.localDB.userGeometryFiles, async () => {
        await this.localDB.userGeometries.bulkDelete(ids)
        await this.localDB.userGeometryFiles.bulkDelete(fileKeys)
      })
      return Result.ok()
    } catch (error) {
      return Result.fail(`Failed to hard-delete user geometries: ${error}`)
    }
  }

  async clearLocalData(): Promise<Result<void>> {
    try {
      await this.localDB.transaction('rw', this.localDB.userGeometries, this.localDB.userGeometryFiles, async () => {
        await this.localDB.userGeometries.clear()
        await this.localDB.userGeometryFiles.clear()
      })
      clearFlatGeobufReadCache()
      return Result.ok()
    } catch (error) {
      return Result.fail(`Failed to clear user geometry data: ${error}`)
    }
  }

  // Mapping helpers ----------------------------------------------------------

  private toRow = (geometry: UserGeometry): DexieUserGeometry => ({
    id: geometry.id.toString(),
    name: geometry.name,
    originalFileName: geometry.originalFileName,
    createdAt: geometry.createdAt,
    updatedAt: geometry.updatedAt,
    featureCount: geometry.featureCount,
    synced: geometry.synced ? 1 : 0,
    deleted: geometry.deleted ? 1 : 0,
    originalSynced: geometry.originalSynced ? 1 : 0,
    processedSynced: geometry.processedSynced ? 1 : 0
  })

  private toEntity = (row: DexieUserGeometry): UserGeometry =>
    UserGeometry.create(
      {
        name: row.name,
        originalFileName: row.originalFileName,
        createdAt: row.createdAt,
        updatedAt: row.updatedAt,
        featureCount: row.featureCount,
        synced: row.synced === 1,
        deleted: row.deleted === 1,
        originalSynced: row.originalSynced === 1,
        processedSynced: row.processedSynced === 1
      },
      new UniqueEntityID(row.id)
    )
}
